import React, { useCallback, useMemo, useState } from 'react';
import { Box, Link, Typography, Chip, ButtonBase } from '@mui/material';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { ICampaign, CampaignPlatform } from '../../redux/buzzbridge/types';
import type { RootState } from '../../redux/root-reducer';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getPublicCampaignRoleSubtitle } from '../../utils/campaignParticipationGate';
import { marketingTermsOfServiceUrl } from '../../utils/marketingSiteUrls';
import { CampaignAppInstallPayoutPanel, CampaignCreatorPayoutFeePanel } from './CampaignFeeBreakdownPanels';
import { CampaignReferralEarnSidebar } from './CampaignReferralEarnSidebar';
import { BrandReviewsDrawer } from './BrandReviewsDrawer';
import { AccountSubscriptionModal } from '../../pages/AccountSettings/AccountSubscriptionModal';
import { formatCurrency } from '../../constants/currency';
import { useCreatorCampaignPayoutDisplay } from '../../hook/useCreatorCampaignPayoutDisplay';
import { CreatorPayoutAmount } from './CreatorPayoutAmount';
import { getOffsiteCampaignDetails } from '../../redux/buzzbridge/actions';
import { useAuth } from '../../module/AuthContext';
import { premiumCampaignUnlockEntitlements } from '../../utils/planEntitlements';
import { resolveCampaignCreatorRequirements } from '../../utils/ugcCampaignConfig';
import type { IInfluencerTier } from '../../types/global/helper';
import { formatCpaDollarsFromCents } from '../../utils/circuulCpaRates';

interface Props {
    campaign: ICampaign;
}

const platformLabel = (p: CampaignPlatform) => p.charAt(0).toUpperCase() + p.slice(1);

function formatBrandRating(n?: number | null): string {
    if (n == null || !Number.isFinite(Number(n))) return '—';
    return Number(n).toFixed(1);
}

export const CampaignDetailRoles: React.FC<Props> = ({ campaign }) => {
    const dispatch = useDispatch();
    const { themeStyle } = useThemeContext();
    const { isSignedIn } = useAuth();
    const t = theme[themeStyle];
    const platforms = campaign.platforms ?? [];
    const brand = typeof campaign.brand === 'object' ? campaign.brand : null;
    const roleSubtitle = getPublicCampaignRoleSubtitle(campaign);
    const configTiers = useSelector((state: RootState) => state.configuration.influencerTiers) as IInfluencerTier[];
    const creatorRequirements = useMemo(
        () => resolveCampaignCreatorRequirements(campaign, configTiers),
        [campaign, configTiers],
    );
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
    const [reviewsOpen, setReviewsOpen] = useState(false);
    const {
        breakdown: creatorPayoutBreakdown,
        netAmountFormatted,
        estimatedTotalFormatted,
        basePayFormatted,
        estimatedCpaFormatted,
        basePayCents,
        clientHasActiveSubscription,
        isAppInstallCpa,
        appInstallEstimate,
        appInstallKindLabel,
    } = useCreatorCampaignPayoutDisplay(campaign);
    const saveAmountFormatted = useMemo(() => {
        if (isAppInstallCpa && appInstallEstimate) {
            if (appInstallEstimate.extraIfSubscribedCents > 0) {
                return formatCpaDollarsFromCents(appInstallEstimate.extraIfSubscribedCents);
            }
            return undefined;
        }
        const saveUsd = creatorPayoutBreakdown?.subscribe_to_save_usd;
        return saveUsd != null && Number.isFinite(saveUsd) ? formatCurrency(saveUsd) : undefined;
    }, [appInstallEstimate, creatorPayoutBreakdown?.subscribe_to_save_usd, isAppInstallCpa]);
    const premiumUnlockEntitlements = useMemo(
        () => premiumCampaignUnlockEntitlements(campaign.premium_access),
        [campaign.premium_access],
    );

    const openSubscriptionModal = useCallback(() => {
        setSubscriptionModalOpen(true);
    }, []);

    const closeSubscriptionModal = useCallback(() => {
        setSubscriptionModalOpen(false);
    }, []);

    const handleSubscriptionDone = useCallback(() => {
        setSubscriptionModalOpen(false);
        if (campaign._id) {
            dispatch(getOffsiteCampaignDetails({ campaignId: campaign._id }));
        }
    }, [campaign._id, dispatch]);

    const brandAvatar = brand?.profile_picture || brand?.logo;
    const reviewCount = brand?.number_of_reviews ?? 0;
    const hasReviews = reviewCount > 0 && brand?.rating != null;

    return (
        <>
            <Box sx={{ position: 'sticky', top: 96, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.15em',
                        color: t.highlight,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                    }}>
                    Open Roles
                </Typography>

                {/* Creator role card */}
                <Box
                    sx={{
                        p: 3,
                        bgcolor: t.card_grey,
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': { bgcolor: t.wrapperColor },
                        '&:hover .role-title': { color: t.highlight },
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                            <Typography
                                className="role-title"
                                sx={{
                                    fontSize: 18,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: t.true_white,
                                    transition: 'color 0.3s',
                                }}>
                                UGC Creator
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: t.subtle_white }}>
                                {roleSubtitle ?? 'Open for applications'}
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 20,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: roleSubtitle ? t.subtle_white : t.highlight,
                            }}>
                            <CreatorPayoutAmount
                                total={estimatedTotalFormatted}
                                base={basePayFormatted}
                                additional={estimatedCpaFormatted}
                                fallback={netAmountFormatted ?? campaign.possible_earning}
                            />
                        </Typography>
                    </Box>

                    {creatorRequirements ? (
                        <Box
                            sx={{
                                mb: 2.5,
                                p: 1.75,
                                borderRadius: 2,
                                bgcolor: 'rgba(255,201,101,0.06)',
                                border: '1px solid rgba(255,201,101,0.22)',
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <GroupsOutlinedIcon sx={{ fontSize: 16, color: t.highlight }} />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        color: t.highlight,
                                    }}>
                                    Requirements
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {creatorRequirements.lines.map(line => (
                                    <Typography
                                        key={line}
                                        sx={{
                                            fontSize: 13,
                                            lineHeight: 1.45,
                                            color: 'rgba(255,255,255,0.88)',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {line}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    ) : null}

                    {isAppInstallCpa && appInstallEstimate ? (
                        <Box sx={{ mb: 2.5 }}>
                            <CampaignAppInstallPayoutPanel
                                estimate={appInstallEstimate}
                                kindLabel={appInstallKindLabel ?? 'install'}
                                basePayCents={basePayCents}
                                onViewPlansClick={openSubscriptionModal}
                            />
                        </Box>
                    ) : (
                        creatorPayoutBreakdown && (
                            <Box sx={{ mb: 2.5 }}>
                                <CampaignCreatorPayoutFeePanel
                                    breakdown={creatorPayoutBreakdown}
                                    viewerHasActiveSubscription={clientHasActiveSubscription}
                                    onViewPlansClick={openSubscriptionModal}
                                />
                            </Box>
                        )
                    )}
                    <Typography
                        sx={{
                            mb: campaign.exclusive_rights ? 1.25 : 2.5,
                            fontSize: 12,
                            lineHeight: 1.5,
                            color: t.tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}
                        onClick={e => e.stopPropagation()}>
                        By applying you accept the terms of this engagement
                        {campaign.exclusive_rights ? ', including exclusive commercial usage rights for 12 months' : ''}
                        . You also agree to our terms of service and payment governance{' '}
                        <Link
                            href={`${marketingTermsOfServiceUrl()}#campaign-payment-refund-payout-policy`}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                                color: t.highlight,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                cursor: 'pointer',
                            }}>
                            Learn more
                        </Link>
                    </Typography>
                    {campaign.exclusive_rights ? (
                        <Box
                            sx={{
                                mb: 2.5,
                                p: 1.75,
                                borderRadius: '12px',
                                border: `1px solid ${t.highlight}44`,
                                bgcolor: `${t.highlight}14`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: t.highlight,
                                    mb: 0.5,
                                }}>
                                Exclusive rights
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    lineHeight: 1.5,
                                    color: t.tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Full commercial usage for 12 months. By applying, you grant the brand exclusive
                                commercial rights to content you submit for this campaign.
                            </Typography>
                        </Box>
                    ) : null}
                    {(platforms.length > 0 || campaign.reward || creatorRequirements) && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {creatorRequirements ? (
                                <Chip
                                    label={creatorRequirements.tierName}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255,201,101,0.14)',
                                        color: t.highlight,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                    }}
                                />
                            ) : null}
                            {platforms.map(p => (
                                <Chip
                                    key={p}
                                    label={platformLabel(p)}
                                    size="small"
                                    sx={{
                                        bgcolor: t.deep_grey_lighter,
                                        color: t.true_white,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                    }}
                                />
                            ))}
                            {campaign.reward && (
                                <Chip
                                    label={campaign.reward}
                                    size="small"
                                    sx={{
                                        bgcolor: t.deep_grey_lighter,
                                        color: t.true_white,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                    }}
                                />
                            )}
                        </Box>
                    )}
                </Box>

                {campaign.referral_earnings_if_friend_hired?.enabled && campaign._id ? (
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <CampaignReferralEarnSidebar
                            campaignId={campaign._id}
                            earnings={campaign.referral_earnings_if_friend_hired}
                            isSignedIn={Boolean(isSignedIn)}
                        />
                    </Box>
                ) : null}

                {/* Brand info */}
                {brand && (
                    <Box
                        sx={{
                            p: 3,
                            bgcolor: t.card_grey,
                            borderRadius: 3,
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25 }}>
                            {brandAvatar ? (
                                <Box
                                    component="img"
                                    src={brandAvatar}
                                    alt={brand.name}
                                    sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                                />
                            ) : null}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ color: t.true_white, fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14 }}>
                                    {brand.name}
                                </Typography>
                                <Typography sx={{ color: t.subtle_white, fontSize: 12 }}>Campaign Host</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <StarRoundedIcon sx={{ fontSize: 18, color: t.highlight }} />
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 14,
                                        color: hasReviews ? t.highlight : t.subtle_white,
                                    }}>
                                    {hasReviews ? formatBrandRating(brand.rating) : 'No ratings yet'}
                                </Typography>
                                {hasReviews ? (
                                    <Typography sx={{ fontSize: 12, color: t.subtle_white, ml: 0.25 }}>
                                        ({reviewCount})
                                    </Typography>
                                ) : null}
                            </Box>
                            {brand._id ? (
                                <ButtonBase
                                    onClick={() => setReviewsOpen(true)}
                                    sx={{
                                        color: t.highlight,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 1,
                                    }}>
                                    View reviews
                                </ButtonBase>
                            ) : null}
                        </Box>
                    </Box>
                )}
            </Box>

            <AccountSubscriptionModal
                open={subscriptionModalOpen}
                onClose={closeSubscriptionModal}
                onDone={handleSubscriptionDone}
                saveAmountFormatted={saveAmountFormatted}
                campaignImageUrl={campaign.feature_image ?? campaign.assets?.[0]?.url}
                requireAllEntitlements={premiumUnlockEntitlements}
            />

            {brand?._id ? (
                <BrandReviewsDrawer open={reviewsOpen} onClose={() => setReviewsOpen(false)} brand={brand} />
            ) : null}
        </>
    );
};
